import { cva } from 'class-variance-authority'
import classNames from 'classnames'
import { InputHTMLAttributes, forwardRef, ReactNode } from 'react'

interface PropTypes extends InputHTMLAttributes<HTMLInputElement> {
  prefixIcon?: ReactNode
  postfix?: ReactNode
  label?: string
  placeholder?: string
  error?: string
  hasError?: boolean
  containerClassName?: string
  variant?: 'default' | 'small' | 'xs' | 'xxs' | 'borderless'
}

const inputStyles = cva(
  [
    'w-full peer rounded text-base leading-6 font-medium placeholder-gray-600 border outline-none transition-all absolute inset-0 appearance-none',
    'focus:ring-2 focus:ring-green-900 focus:border-gray-400',
    '[appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none',
  ],
  {
    variants: {
      error: {
        true: 'text-error-800 border-error-500',
        false: 'text-gray-700 border-gray-400',
      },
      variant: {
        default: 'p-[12px]',
        small: 'p-[10px]',
        xs: 'p-[10px]',
        xxs: '!py-[5.5px] !px-3 !text-[14px]',
        borderless: 'border-0',
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  },
)

const wrapperStyles = cva(
  'relative w-full',
  {
    variants: {
      variant: {
        default: 'h-11',
        small: 'h-[35px]',
        xs: 'h-[30px]',
        xxs: 'h-[30px]',
        borderless: 'h-[35px]',
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  },
)

export default forwardRef<HTMLInputElement, PropTypes>(function TextInput ({
  prefixIcon,
  postfix,
  name,
  error,
  hasError = false,
  placeholder,
  variant,
  ...rest
}, ref) {
  return (
    <div className="flex flex-col items-start">
      <div className={wrapperStyles({ variant })}>
        {
          prefixIcon
          && (
            <div className="absolute inset-0 z-10 flex items-center h-full ml-3 text-sm pointer-events-none">
              { prefixIcon }
            </div>
          )
        }
        <input
          ref={ref}
          name={name}
          placeholder={placeholder}
          {...rest}
          className={classNames(inputStyles({ error: Boolean(error) || hasError, variant }), {
            'indent-8': prefixIcon,
            'pr-10': postfix,
          })}
        />
        {
          postfix
          && (
            <div className="absolute right-0 z-10 flex items-center h-full mr-3 text-sm pointer-events-none">
              {postfix}
            </div>
          )
        }
      </div>
      {
        error
        && <p className="text-red-700 mt-2 text-[13px] leading-[15.6px]">
          {error}
        </p>
      }
    </div>
  )
})
